import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, Star, Clock, Eye, Heart, ExternalLink,
  Package, Sparkles, Zap, Award, TrendingUp, Users, Download, Lightbulb,
  ChevronRight, Check, X, Info, Tag, Layers, Calculator, Palette, 
  ShoppingBag, Store, Coffee, Pill, Shirt, Home, Building2,
  Briefcase, Car, School, Hotel, Gift, Dumbbell, BookOpen, Camera,
  Shield, Truck, Settings, Phone, Mail, MapPin, Globe, Ruler, Box
} from 'lucide-react';
import { PageWrapper, Card, EmptyState, StatCard } from '@/components/ui/PageWrapper';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

// RZECZYWISTE PRODUKTY PLEXISYSTEM
const products = [
  {
    id: 'exp-kosmetyki-led',
    name: 'Ekspozytor Kosmetyczny Premium LED',
    description: 'Profesjonalny ekspozytor na kosmetyki z podświetleniem LED edge. Idealny do prezentacji perfum, kremów i produktów premium.',
    category: 'Ekspozytory',
    industry: ['Kosmetyki', 'Perfumerie', 'Drogerie'],
    image: 'https://images.unsplash.com/photo-1570194065650-d99fb4b38e39?w=800',
    features: [
      'Podświetlenie LED edge w technologii światłowodowej',
      '5 poziomów ekspozycji z regulacją wysokości',
      'Wymienne panele brandingowe',
      'System antykradzieżowy opcjonalnie',
      'Możliwość grawerowania logo laserem'
    ],
    materials: {
      primary: 'Plexi ekstrudowane bezbarwne',
      thickness: '5mm / 8mm',
      options: ['Plexi mleczne', 'Plexi satynowe', 'Dibond w podstawie']
    },
    dimensions: {
      standard: '60x40x180 cm',
      customizable: true
    },
    priceRange: { min: 2800, max: 4500, unit: 'szt' },
    leadTime: '10-14 dni',
    minOrder: 1,
    popularity: 95,
    tags: ['LED', 'premium', 'kosmetyki', 'podświetlenie'],
    applications: ['Perfumerie', 'Salony kosmetyczne', 'Apteki', 'Galerie handlowe'],
    customization: ['Wymiary', 'Kolor LED', 'Grawerowanie logo', 'Dodatkowe półki']
  },
  {
    id: 'gab-piekarnicza',
    name: 'Gablota Piekarniczo-Cukiernicza HACCP',
    description: 'Higieniczna gablota ekspozycyjna do produktów spożywczych. Spełnia normy HACCP.',
    category: 'Gabloty',
    industry: ['Gastronomia', 'Retail'],
    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800',
    features: [
      'Certyfikat kontaktu z żywnością',
      'Osłona higieniczna z dostępem od tyłu',
      '3-4 poziomy regulowane co 10cm',
      'Wentylacja grawitacyjna',
      'Łatwe czyszczenie - gładkie powierzchnie'
    ],
    materials: {
      primary: 'Plexi ekstrudowane bezbarwne',
      thickness: '4-5mm',
      options: ['Podstawa ze stali nierdzewnej', 'Podstawa z Corianu']
    },
    dimensions: {
      standard: '90x50x60 cm',
      customizable: true
    },
    priceRange: { min: 1200, max: 2400, unit: 'szt' },
    leadTime: '5-7 dni',
    minOrder: 1,
    popularity: 93,
    tags: ['gastronomia', 'HACCP', 'higiena', 'piekarnia'],
    applications: ['Piekarnie', 'Cukiernie', 'Kawiarnie', 'Delikatesy'],
    customization: ['Wymiary według lady', 'Liczba półek', 'Rodzaj otwarcia', 'Oświetlenie LED']
  },
  {
    id: 'kas-led-dwustronny',
    name: 'Kaseton LED Dwustronny IP65',
    description: 'Profesjonalny kaseton reklamowy z podświetleniem LED. Energooszczędny, wodoodporny.',
    category: 'Kasetony',
    industry: ['Wszystkie'],
    image: 'https://images.unsplash.com/photo-1562113607-44e5aa661e16?w=800',
    features: [
      'Podświetlenie LED edge lub face',
      'Wodoodporność IP65',
      'Rama aluminiowa malowana proszkowo',
      'Lico z plexi mlecznej lub transparentnej',
      'Grafika drukowana UV lub foliowana'
    ],
    materials: {
      primary: 'Plexi opalowa + aluminium',
      thickness: '3mm plexi, rama 10cm',
      options: ['Dibond', 'Plexi transparentna']
    },
    dimensions: {
      standard: '100x70x10 cm',
      customizable: true
    },
    priceRange: { min: 1500, max: 3500, unit: 'szt' },
    leadTime: '7-10 dni',
    minOrder: 1,
    popularity: 91,
    tags: ['LED', 'reklama', 'outdoor', 'kaseton'],
    applications: ['Reklama zewnętrzna', 'Oznakowanie sklepów', 'Galerie handlowe'],
    customization: ['Dowolne wymiary', 'Kształt', 'Typ podświetlenia', 'Kolor ramy RAL']
  },
  {
    id: 'lit-3d-led',
    name: 'Litery 3D LED Przestrzenne',
    description: 'Litery przestrzenne z plexi z podświetleniem LED. Głębokość od 3 do 20cm.',
    category: 'Litery 3D',
    industry: ['Wszystkie'],
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800',
    features: [
      'Front z plexi lub dibondu',
      'Boki z PCV lub aluminium',
      'Podświetlenie frontowe lub halo',
      'Montaż na dystansach',
      'Wodoodporność IP65'
    ],
    materials: {
      primary: 'Plexi + PCV/Aluminium',
      thickness: 'Front 3-10mm, głębokość 3-20cm',
      options: ['Dibond', 'Stal nierdzewna']
    },
    dimensions: {
      standard: 'Wysokość 20-150 cm',
      customizable: true
    },
    priceRange: { min: 150, max: 450, unit: 'litera' },
    leadTime: '10-14 dni',
    minOrder: 1,
    popularity: 89,
    tags: ['litery', '3D', 'LED', 'logo'],
    applications: ['Logo firm', 'Nazwy sklepów', 'Recepcje', 'Targi'],
    customization: ['Font', 'Głębokość', 'Kolor LED', 'Wykończenie frontu']
  },
  {
    id: 'stojak-ulotkowy',
    name: 'Stojak na Ulotki A4/A5',
    description: 'Elegancki stojak na materiały reklamowe z kieszeniami kaskadowymi.',
    category: 'Stojaki',
    industry: ['Biura', 'Retail', 'Usługi'],
    image: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=800',
    features: [
      '3-6 kieszeni A4 lub A5',
      'Układ kaskadowy dla lepszej widoczności',
      'Stabilna podstawa obciążona',
      'Krawędzie polerowane',
      'Możliwość brandingu'
    ],
    materials: {
      primary: 'Plexi transparentne',
      thickness: '3mm',
      options: ['Podstawa z MDF', 'Całość z plexi']
    },
    dimensions: {
      standard: '23x30x140 cm (A4)',
      customizable: true
    },
    priceRange: { min: 280, max: 520, unit: 'szt' },
    leadTime: '3-5 dni',
    minOrder: 1,
    popularity: 87,
    tags: ['ulotki', 'A4', 'stojak', 'kaskada'],
    applications: ['Recepcje', 'Banki', 'Przychodnie', 'Sklepy'],
    customization: ['Liczba kieszeni', 'Format', 'Orientacja', 'Grawerowanie logo']
  },
  {
    id: 'oslona-kasowa',
    name: 'Osłona Kasowa z Okienkiem',
    description: 'Osłona ochronna dla kas i recepcji. Okienko podawcze, łatwa dezynfekcja.',
    category: 'Osłony',
    industry: ['Retail', 'Biura', 'Apteki'],
    image: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800',
    features: [
      'Okienko podawcze 30x15cm',
      'Stabilne nóżki lub mocowanie do blatu',
      'Zaokrąglone krawędzie',
      'Łatwa dezynfekcja',
      'Półka na dokumenty opcjonalnie'
    ],
    materials: {
      primary: 'Plexi transparentne',
      thickness: '4-5mm',
      options: ['Plexi antyrefleksyjne', 'Podstawa metalowa']
    },
    dimensions: {
      standard: '80x60 cm',
      customizable: true
    },
    priceRange: { min: 320, max: 680, unit: 'szt' },
    leadTime: '2-3 dni',
    minOrder: 1,
    popularity: 94,
    tags: ['osłona', 'kasa', 'ochrona', 'COVID'],
    applications: ['Kasy sklepowe', 'Recepcje', 'Apteki', 'Banki', 'Urzędy'],
    customization: ['Wymiary do lady', 'Wielkość okienka', 'Mocowanie', 'Grawerowanie logo']
  },
  {
    id: 'totem-led',
    name: 'Totem Reklamowy LED 200cm',
    description: 'Wolnostojący totem reklamowy z podświetleniem LED. Dwustronny, podstawa obciążona.',
    category: 'Totemy',
    industry: ['Wszystkie'],
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800',
    features: [
      'Wysokość 200cm',
      'Dwustronna ekspozycja',
      'Podświetlenie LED całości',
      'Podstawa obciążona 40kg',
      'Łatwa wymiana grafiki'
    ],
    materials: {
      primary: 'Aluminium + plexi',
      thickness: 'Rama 10cm, plexi 4mm',
      options: ['Dibond', 'Podstawa betonowa']
    },
    dimensions: {
      standard: '60x200x40 cm',
      customizable: true
    },
    priceRange: { min: 3200, max: 5800, unit: 'szt' },
    leadTime: '10-14 dni',
    minOrder: 1,
    popularity: 88,
    tags: ['totem', 'LED', 'wolnostojący', 'reklama'],
    applications: ['Centra handlowe', 'Salony samochodowe', 'Targi', 'Recepcje'],
    customization: ['Wysokość', 'Szerokość', 'Kształt', 'Kolor LED', 'Podstawa']
  },
  {
    id: 'organizer-kosm',
    name: 'Organizer Kosmetyczny Modułowy',
    description: 'System organizerów do kosmetyków z możliwością łączenia modułów.',
    category: 'Organizery',
    industry: ['Kosmetyki', 'Dom'],
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800',
    features: [
      'System modułowy - łączenie elementów',
      'Różne wysokości przegród',
      'Szuflady na drobne akcesoria',
      'Antypoślizgowe nóżki',
      'Łatwe czyszczenie'
    ],
    materials: {
      primary: 'Plexi transparentne',
      thickness: '3mm',
      options: ['Plexi kolorowe', 'Wykończenie satynowe']
    },
    dimensions: {
      standard: '20x15x10 cm (moduł)',
      customizable: true
    },
    priceRange: { min: 85, max: 220, unit: 'moduł' },
    leadTime: '3-5 dni',
    minOrder: 1,
    popularity: 90,
    tags: ['organizer', 'kosmetyki', 'modułowy'],
    applications: ['Salony kosmetyczne', 'Drogerie', 'Użytek domowy', 'Hotele'],
    customization: ['Rozmiar modułów', 'Liczba przegród', 'Grawerowanie', 'Kolor']
  }
];

const categories = [
  { id: 'all', name: 'Wszystkie', icon: Package },
  { id: 'Ekspozytory', name: 'Ekspozytory', icon: Store },
  { id: 'Gabloty', name: 'Gabloty', icon: Box },
  { id: 'Kasetony', name: 'Kasetony', icon: Lightbulb },
  { id: 'Litery 3D', name: 'Litery 3D', icon: Tag },
  { id: 'Stojaki', name: 'Stojaki', icon: BookOpen },
  { id: 'Osłony', name: 'Osłony', icon: Shield },
  { id: 'Totemy', name: 'Totemy', icon: Building2 },
  { id: 'Organizery', name: 'Organizery', icon: Layers }
];

const industries = [
  { id: 'all', name: 'Wszystkie', icon: Globe },
  { id: 'Kosmetyki', name: 'Kosmetyki', icon: Palette },
  { id: 'Retail', name: 'Retail', icon: ShoppingBag },
  { id: 'Gastronomia', name: 'Gastronomia', icon: Coffee },
  { id: 'Apteki', name: 'Apteki', icon: Pill },
  { id: 'Biura', name: 'Biura', icon: Briefcase },
  { id: 'Wszystkie', name: 'Universal', icon: Globe }
];

export function Marketplace() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [savedProducts, setSavedProducts] = useState<string[]>([]);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesIndustry = selectedIndustry === 'all' || 
      product.industry.includes(selectedIndustry) || 
      product.industry.includes('Wszystkie');
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesIndustry && matchesSearch;
  });

  const toggleSaved = (productId: string) => {
    setSavedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleCalculatePrice = (product: any) => {
    navigate('/calculator-pro', { 
      state: { fromMarketplace: true, product }
    });
  };

  const handleContact = (product: any) => {
    const subject = encodeURIComponent(`Zapytanie: ${product.name}`);
    const body = encodeURIComponent(
      `Dzień dobry,\n\nJestem zainteresowany: ${product.name}\n\n` +
      `Proszę o wycenę.\n\nPozdrawiam`
    );
    window.open(`mailto:biuro@plexisystem.pl?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <PageWrapper>
      {/* Hero */}
      <Card className="mb-8 p-8 bg-gradient-to-r from-purple-900/20 via-blue-900/20 to-purple-900/20 border-purple-500/20">
        <div className="max-w-4xl">
          <h1 className="text-4xl font-bold text-white mb-4 flex items-center gap-3">
            <Store className="w-10 h-10 text-purple-400" />
            Katalog Produktów PlexiSystem B2B
          </h1>
          <p className="text-lg text-gray-300 mb-6">
            Profesjonalne rozwiązania z plexi, PMMA, PCV i dibondu. Produkcja na zamówienie 
            z własnego parku maszynowego. Realizacja 3-14 dni.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button variant="primary" onClick={() => window.open('https://plexisystem.pl', '_blank')}>
              <Globe className="w-5 h-5" />
              Nasza strona
            </Button>
            <Button variant="secondary" onClick={() => navigate('/calculator-pro')}>
              <Calculator className="w-5 h-5" />
              Kalkulator
            </Button>
            <Button variant="secondary" onClick={() => window.open('tel:+48503558999', '_self')}>
              <Phone className="w-5 h-5" />
              +48 503 558 999
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Package className="w-6 h-6" />} label="Produktów" value={products.length} color="purple" />
        <StatCard icon={<Truck className="w-6 h-6" />} label="Realizacja" value="3-14 dni" color="green" />
        <StatCard icon={<Award className="w-6 h-6" />} label="Doświadczenie" value="15+ lat" color="amber" />
        <StatCard icon={<MapPin className="w-6 h-6" />} label="Dostawa" value="Polska" color="blue" />
      </div>

      {/* Search & Filters */}
      <Card className="p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj produktów..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-zinc-800/50 text-gray-300 hover:bg-zinc-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Industries */}
        <div className="flex flex-wrap gap-2">
          {industries.map(ind => {
            const Icon = ind.icon;
            return (
              <button
                key={ind.id}
                onClick={() => setSelectedIndustry(ind.id)}
                className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                  selectedIndustry === ind.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-800/50 text-gray-300 hover:bg-zinc-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {ind.name}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Info */}
      <Card className="p-6 mb-8 bg-blue-900/20 border-blue-500/20">
        <div className="flex items-start gap-4">
          <Info className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Produkcja na zamówienie</h3>
            <p className="text-gray-300 mb-3">
              Wszystkie produkty wykonujemy indywidualnie. Dostosowujemy wymiary, materiały i kolory. 
              Własny park maszynowy: frezarki CNC, lasery CO₂, giętarki, drukarki UV.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Wymiary custom</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Logo/branding</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Różne materiały</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Krótkie terminy</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <Card className="p-12">
          <EmptyState
            icon={<Package className="w-12 h-12" />}
            title="Brak produktów"
            description="Nie znaleziono produktów"
            action={
              <Button onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedIndustry('all');
              }} variant="secondary">
                Wyczyść filtry
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="overflow-hidden h-full hover:border-purple-500/50 transition-all">
                <div 
                  className="h-56 relative overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-900 cursor-pointer group"
                  onClick={() => setSelectedProduct(product)}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 backdrop-blur rounded-full">
                    <span className="text-xs text-white font-medium">{product.category}</span>
                  </div>
                  
                  <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur rounded-full">
                    <span className="text-xs text-white flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-400 fill-current" />
                      {product.popularity}%
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-xl font-bold text-white mb-1">{product.name}</h3>
                    <p className="text-sm text-gray-200 line-clamp-2">{product.description}</p>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Layers className="w-4 h-4" />
                      {product.materials.primary}
                    </span>
                    <span className="flex items-center gap-1">
                      <Ruler className="w-4 h-4" />
                      {product.dimensions.standard}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Cena netto</p>
                      <p className="text-lg font-semibold text-white">
                        {product.priceRange.min} - {product.priceRange.max} zł
                        <span className="text-xs text-gray-400 ml-1">/{product.priceRange.unit}</span>
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSaved(product.id);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg transition-all"
                    >
                      <Heart 
                        className={`w-5 h-5 ${
                          savedProducts.includes(product.id) 
                            ? 'fill-red-500 text-red-500' 
                            : 'text-gray-400'
                        }`} 
                      />
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>Realizacja: {product.leadTime}</span>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="primary"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCalculatePrice(product);
                      }}
                    >
                      <Calculator className="w-4 h-4" />
                      Wycena
                    </Button>
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProduct(product);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                      Szczegóły
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Product Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-800 rounded-2xl max-w-5xl w-full my-8 border border-zinc-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-zinc-800 border-b border-zinc-700 p-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedProduct.name}</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mt-2">
                    <span>{selectedProduct.category}</span>
                    <span>•</span>
                    <span>{selectedProduct.leadTime}</span>
                    <span>•</span>
                    <span>Min. {selectedProduct.minOrder} {selectedProduct.priceRange.unit}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="p-2 hover:bg-zinc-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="h-96 bg-gradient-to-br from-zinc-700 to-zinc-900 rounded-xl overflow-hidden mb-4">
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="bg-zinc-700/50 rounded-xl p-4">
                      <h3 className="text-lg font-semibold text-white mb-2">Opis</h3>
                      <p className="text-gray-300 mb-4">{selectedProduct.description}</p>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-2">Zastosowania:</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProduct.applications.map((app: string, idx: number) => (
                            <span key={idx} className="px-3 py-1 bg-zinc-600/50 rounded-full text-xs text-gray-300">
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        Cechy produktu
                      </h3>
                      <div className="space-y-2">
                        {selectedProduct.features.map((feature: string, idx: number) => (
                          <div key={idx} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-emerald-400 mt-0.5" />
                            <span className="text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-blue-400" />
                        Materiały
                      </h3>
                      <div className="bg-zinc-700/50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Podstawowy:</span>
                          <span className="text-white">{selectedProduct.materials.primary}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Grubość:</span>
                          <span className="text-white">{selectedProduct.materials.thickness}</span>
                        </div>
                        {selectedProduct.materials.options && (
                          <div className="pt-2 border-t border-zinc-600">
                            <span className="text-gray-400 block mb-2">Opcje:</span>
                            <div className="flex flex-wrap gap-2">
                              {selectedProduct.materials.options.map((opt: string, idx: number) => (
                                <span key={idx} className="px-2 py-1 bg-zinc-600/50 rounded text-xs text-gray-300">
                                  {opt}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-pink-400" />
                        Personalizacja
                      </h3>
                      <div className="grid grid-cols-1 gap-2">
                        {selectedProduct.customization.map((option: string, idx: number) => (
                          <div key={idx} className="flex items-center gap-2 text-gray-300">
                            <div className="w-2 h-2 bg-purple-400 rounded-full" />
                            <span className="text-sm">{option}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-zinc-700 pt-6">
                      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/20">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Cena netto</p>
                            <p className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                              {selectedProduct.priceRange.min} - {selectedProduct.priceRange.max} zł
                              <span className="text-lg text-gray-400 ml-2">/{selectedProduct.priceRange.unit}</span>
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button 
                            variant="primary" 
                            className="flex-1"
                            onClick={() => {
                              handleCalculatePrice(selectedProduct);
                              setSelectedProduct(null);
                            }}
                          >
                            <Calculator className="w-5 h-5" />
                            Stwórz wycenę
                          </Button>
                          <Button 
                            variant="secondary"
                            onClick={() => handleContact(selectedProduct)}
                          >
                            <Mail className="w-5 h-5" />
                            Zapytaj
                          </Button>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-3 gap-4">
                        <div className="bg-zinc-700/30 rounded-lg p-4 text-center">
                          <Phone className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                          <a href="tel:+48503558999" className="text-white hover:text-purple-400">
                            +48 503 558 999
                          </a>
                        </div>
                        <div className="bg-zinc-700/30 rounded-lg p-4 text-center">
                          <Mail className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                          <a href="mailto:biuro@plexisystem.pl" className="text-white hover:text-purple-400 text-sm">
                            biuro@plexisystem.pl
                          </a>
                        </div>
                        <div className="bg-zinc-700/30 rounded-lg p-4 text-center">
                          <Globe className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                          <a href="https://plexisystem.pl" target="_blank" rel="noopener noreferrer" className="text-white hover:text-purple-400">
                            plexisystem.pl
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
}